import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ContentCopyOutlined,
  DeleteOutline,
  Edit,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import SelectField from "../../atoms/SelectField/SelectField";
import {
  deleteSatisfactionSurveyTemplate,
  duplicateSatisfactionSurveyTemplate,
  findAllSatisfactionSurveyTemplates,
} from "../../../API/apiSatisfactionSurveyTemplates";
import { SatisfactionSurveyTemplate } from "../../../types/SatisfactionSurveyTemplate";
import styles from "./ModelosPesquisa.module.css";
import stylesFilter from "../../../styles/Filter.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesTable from "../../../styles/Table.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";
import stylesGrid from "../../../styles/Grid.module.css";

const ModelosPesquisa = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SatisfactionSurveyTemplate[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingConfirmDialog, setLoadingConfirmDialog] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [searchTitle, setSearchTitle] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SatisfactionSurveyTemplate | null>(null);

  useEffect(() => {
    getTemplates();
  }, [currentPage, perPage]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  const getTemplates = async () => {
    setLoading(true);
    try {
      const response = await findAllSatisfactionSurveyTemplates(currentPage, perPage);
      setTemplates(response.satisfaction_survey_templates || []);
      setTotalPages(response.pagination?.last_page || 1);
    } catch (error) {
      setMsgError("Nao foi possivel listar os modelos de pesquisa.");
    } finally {
      setLoading(false);
    }
  };

  const openView = (template: SatisfactionSurveyTemplate) => {
    setSelectedTemplate(template);
    setOpenViewDialog(true);
  };

  const openDuplicate = (template: SatisfactionSurveyTemplate) => {
    setSelectedTemplate(template);
    setOpenDuplicateDialog(true);
  };

  const openDelete = (template: SatisfactionSurveyTemplate) => {
    setSelectedTemplate(template);
    setOpenDeleteDialog(true);
  };

  const closeActionDialogs = () => {
    setOpenDuplicateDialog(false);
    setOpenDeleteDialog(false);
    setLoadingConfirmDialog(false);
    setSelectedTemplate(null);
  };

  const handleDuplicate = async () => {
    if (!selectedTemplate) return;

    try {
      setLoadingConfirmDialog(true);
      await duplicateSatisfactionSurveyTemplate(selectedTemplate.id);
      setMsgSuccess("Modelo de pesquisa duplicado com sucesso.");
      setOpenDuplicateDialog(false);
      setSelectedTemplate(null);
      await getTemplates();
    } catch {
      setOpenDuplicateDialog(false);
      setSelectedTemplate(null);
      setMsgError("Nao foi possivel duplicar o modelo de pesquisa.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      setLoadingConfirmDialog(true);
      await deleteSatisfactionSurveyTemplate(selectedTemplate.id);
      setMsgSuccess("Modelo de pesquisa excluido com sucesso.");
      setOpenDeleteDialog(false);
      setSelectedTemplate(null);
      if (displayedTemplates.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await getTemplates();
      }
    } catch {
      setOpenDeleteDialog(false);
      setSelectedTemplate(null);
      setMsgError("Nao foi possivel excluir o modelo de pesquisa.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  const displayedTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchTitle =
        !searchTitle ||
        template.titulo.toLowerCase().includes(searchTitle.trim().toLowerCase());
      const matchStatus =
        !filterStatus ||
        (filterStatus === "active" ? template.active : !template.active);

      return matchTitle && matchStatus;
    });
  }, [filterStatus, searchTitle, templates]);

  const handleChangeStatus = (value: string) => {
    if (value === "Ativo") {
      setFilterStatus("active");
      return;
    }

    if (value === "Inativo") {
      setFilterStatus("inactive");
      return;
    }

    setFilterStatus("");
  };

  const getStatusLabel = (value: string) => {
    if (value === "active") return "Ativo";
    if (value === "inactive") return "Inativo";
    return "";
  };

  const handleClearFilters = () => {
    setSearchTitle("");
    setFilterStatus("");
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleString("pt-BR");
    } catch {
      return date;
    }
  };

  return (
    <div className={styles.card}>
      <div>
        <div className={styles.titleRow}>
          <div className={styles.title}>Modelos de Pesquisa</div>
          <Button
            variant="contained"
            className={stylesButton.buttonSquarePrimary}
            onClick={() => navigate("/cadastrar-modelo-pesquisa")}
          >
            Cadastrar Novo Modelo
          </Button>
        </div>

        {msgSuccess && (
          <div className={stylesTable.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        <div className={stylesFilter.container}>
          <div className={stylesFilter.grid12}>
            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Titulo do modelo</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={searchTitle}
                onChange={(event) => setSearchTitle(event.target.value)}
                InputProps={{
                  className: stylesInput.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={stylesFilter.inputGroup3}>
              <label className={stylesInput.titleInput}>Status</label>
              <SelectField
                label=""
                value={getStatusLabel(filterStatus)}
                onChange={handleChangeStatus}
                options={[{ nome: "Ativo" }, { nome: "Inativo" }]}
                disableTyping
              />
            </div>

            <div className={stylesFilter.inputGroup5}>
              <div className={stylesFilter.containerButton}>
                <div className={`${stylesFilter.lineButton} ${stylesGrid.justifyEnd}`}>
                  <Button
                    variant="contained"
                    onClick={handleClearFilters}
                    className={stylesFilter.buttonClean}
                  >
                    Limpar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={getTemplates}
                    className={stylesFilter.buttonSearch}
                  >
                    Atualizar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {msgError && (
          <div className={stylesTable.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <TableContainer className={stylesTable.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={stylesTable.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Titulo
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Descricao
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">
                    Perguntas
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">
                    Status
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Atualizado em
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">
                    Acoes
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedTemplates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" className={stylesTable.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {displayedTemplates.map((template, index) => (
                  <TableRow
                    key={template.id}
                    className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {template.titulo}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {template.descricao || "-"}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs} align="center">
                      <span className={styles.questionCount}>{template.questions.length}</span>
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs} align="center">
                      {template.active ? "Ativo" : "Inativo"}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {formatDate(template.updated_at)}
                    </TableCell>
                    <TableCell padding="none">
                      <div className={stylesTable.blockActions}>
                        <Tooltip title="Editar modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Editar modelo de pesquisa"
                            onClick={() =>
                              navigate(`/cadastrar-modelo-pesquisa?id=${template.id}`, {
                                state: { template },
                              })
                            }
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Visualizar turmas vinculadas" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Visualizar turmas vinculadas"
                            onClick={() => openView(template)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicar modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Duplicar modelo de pesquisa"
                            onClick={() => openDuplicate(template)}
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Excluir modelo de pesquisa"
                            onClick={() => openDelete(template)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {templates.length > 0 && (
          <div className={`${stylesTable.containerPagination} ${stylesGeneral.mt4}`}>
            <div className={stylesTable.qtdItems}>
              <div className={stylesTable.labelSelect}>Resultados por pagina</div>
              <SelectField
                label=""
                value={perPage.toString()}
                onChange={(value) => setPerPage(Number(value))}
                options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
                disableClearable
                disableTyping
              />
            </div>

            <div className={stylesTable.pagination}>
              <IconButton
                className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Primeira pagina"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <KeyboardDoubleArrowLeftOutlined />
              </IconButton>

              <IconButton
                className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Pagina anterior"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <KeyboardArrowLeftOutlined />
              </IconButton>

              <span className={stylesTable.pages}>
                Pagina {currentPage} de {totalPages}
              </span>

              <IconButton
                className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Proxima pagina"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <KeyboardArrowRightOutlined />
              </IconButton>

              <IconButton
                className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Ultima pagina"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <KeyboardDoubleArrowRightOutlined />
              </IconButton>
            </div>

            <div className={stylesTable.setNumberPage}>
              <div className={stylesTable.labelSelect}>Ir para a pagina</div>
              <SelectField
                label=""
                value={currentPage.toString()}
                onChange={(value) => setCurrentPage(Number(value))}
                options={Array.from({ length: totalPages }, (_, index) => ({
                  nome: String(index + 1),
                }))}
                disableTyping
              />
            </div>
          </div>
        )}

        <Dialog
          open={openViewDialog}
          onClose={() => {
            setOpenViewDialog(false);
            setSelectedTemplate(null);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Turmas vinculadas ao modelo</DialogTitle>
          <DialogContent dividers>
            <div className={styles.linkedClassesWrapper}>
              {selectedTemplate?.turmas?.length ? (
                selectedTemplate.turmas.map((turma) => (
                  <div key={turma.id} className={styles.linkedClassCard}>
                    <div className={styles.linkedClassTitle}>{turma.nome}</div>
                    <div className={styles.linkedClassMeta}>
                      <span>Acao: {turma.acao_nome}</span>
                      <span>
                        Pesquisa obrigatoria para certificado: {turma.obrigatoria_para_certificado ? "Sim" : "Nao"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <Alert severity="info">Este modelo nao possui turmas vinculadas.</Alert>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              className={stylesButton.buttonSquareSecondary}
              onClick={() => {
                setOpenViewDialog(false);
                setSelectedTemplate(null);
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={openDuplicateDialog}
          loading={loadingConfirmDialog}
          onClose={closeActionDialogs}
          onConfirm={handleDuplicate}
          title="Duplicar modelo de pesquisa"
          message={`Deseja duplicar o modelo "${selectedTemplate?.titulo || ""}"?`}
          titleButtonConfirm="Duplicar"
          titleLoadButton="Duplicando..."
        />

        <ConfirmDialog
          open={openDeleteDialog}
          loading={loadingConfirmDialog}
          onClose={closeActionDialogs}
          onConfirm={handleDelete}
          title="Excluir modelo de pesquisa"
          message={`Deseja excluir o modelo "${selectedTemplate?.titulo || ""}"?`}
          titleButtonConfirm="Excluir"
          titleLoadButton="Excluindo..."
        />
      </div>
    </div>
  );
};

export default ModelosPesquisa;
